import React, { useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { enrichExistingCamp, EnrichExistingCampRequest, saveEnrichedCamp } from '../lib/enrichExistingCamp';
import { enrichCampData } from '../lib/aiEnrichmentClient';
import { supabase } from '../lib/supabase';
import { parseListText } from '../lib/textUtils';
import { geocodeAddress } from '../lib/geocoding';
import { convertStateAbbreviationToFullName } from '../lib/campUtils';
import toast from 'react-hot-toast';

interface EnrichmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  campId?: string;
  campName?: string;
  currentWebsite?: string;
  onSuccess: () => void;
  mode?: 'enrich' | 'create'; // 'enrich' for existing camps, 'create' for new camps
}

export default function EnrichmentModal({
  isOpen,
  onClose,
  campId,
  campName,
  currentWebsite,
  onSuccess,
  mode = 'enrich'
}: EnrichmentModalProps) {
  const [website, setWebsite] = useState(currentWebsite || '');
  const [isEnriching, setIsEnriching] = useState(false);
  const [progress, setProgress] = useState('');
  const [progressLogs, setProgressLogs] = useState<string[]>([]);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);
  const [showReview, setShowReview] = useState(false);
  const [enrichmentData, setEnrichmentData] = useState<{
    originalCamp: any;
    updateData: any;
    enrichmentResponse: any;
  } | null>(null);
  const [editedUpdateData, setEditedUpdateData] = useState<any>(null);

  const handleEnrich = async () => {
    if (!website.trim()) {
      setResult({ success: false, message: 'Please enter a website URL' });
      return;
    }

    // Clear all logs and state before starting
    setIsEnriching(true);
    setProgress('');
    setProgressLogs([]);
    setResult(null);

    const handleProgress = (message: string) => {
      const timestamp = new Date().toLocaleTimeString();
      // Truncate very long messages for better readability
      const truncatedMessage = message.length > 200 
        ? message.substring(0, 200) + '...' 
        : message;
      const logEntry = `[${timestamp}] ${truncatedMessage}`;
      setProgressLogs(prev => {
        const newLogs = [...prev, logEntry];
        // Keep only last 100 log entries to prevent memory issues
        return newLogs.slice(-100);
      });
      setProgress(truncatedMessage);
    };

    try {
      if (mode === 'create') {
        // Create new camp + enrich flow
        if (!website.trim()) {
          setResult({
            success: false,
            message: 'Please enter a website URL'
          });
          return;
        }

        handleProgress('🚀 Starting AI enrichment for new camp...');
        
        // IMPORTANT: Don't create placeholder names from domain - they're often wrong
        // The AI must extract the proper camp name from the website content
        // We'll use a generic placeholder only as a last resort
        const placeholderName = 'New Camp';

        handleProgress(`🌐 Analyzing website: ${website.trim()}`);
        handleProgress(`🔍 Starting website scraping and content extraction...`);
        handleProgress(`📋 AI will extract the camp name from website content`);

        // Prepare enrichment request - minimal data, AI will extract everything from website
        // Use generic placeholder - AI should extract the real name
        const enrichmentRequest = {
          campName: placeholderName,
          website: website.trim(),
        };

        // Call the enrichment API - progress messages will come from the client wrapper
        let enrichmentResponse;
        let validationFailed = false;
        let validationError: string | null = null;
        
        try {
          // Use the progress handler directly (no duplication)
          enrichmentResponse = await enrichCampData(enrichmentRequest, handleProgress);
        } catch (error: any) {
          // Handle errors from enrichment API
          if (error.shouldSkip) {
            setResult({
              success: false,
              message: error.message || 'Camp skipped - insufficient content or not a camp'
            });
            return;
          }
          
          // If validation failed (IRRELEVANT_CONTENT or INSUFFICIENT_CONTENT), 
          // still try to save the camp but mark it as inactive
          if (error.message?.includes('IRRELEVANT_CONTENT') || error.message?.includes('INSUFFICIENT_CONTENT')) {
            validationFailed = true;
            validationError = error.message;
            handleProgress('⚠️ Validation failed, but continuing to save camp (will be marked as inactive for review)');
            
            // Try to get partial enrichment data if available
            // The error might have been thrown after some data was extracted
            // We'll continue with whatever we have
            console.warn('⚠️ Validation failed, but saving camp anyway:', validationError);
          } else {
            // For other errors, re-throw
            throw error;
          }
        }

        // If validation failed, we still want to save the camp (marked inactive) for manual review
        // Create a minimal enrichmentResponse if validation failed early and we have no data
        if (validationFailed && !enrichmentResponse) {
          // Validation failed before we got any enrichment data
          // Create a minimal response with just the website URL so we can save something
          // Store validation info in enrichment_metadata (NOT in notes - notes are visible to users)
          enrichmentResponse = {
            description: null,
            short_description: null,
            aiExtractedName: null,
            facebookName: null,
            address: null,
            city: null,
            state: null,
            zip_code: null,
            phone: null,
            email: null,
            activities: [],
            amenities: [],
            dates_available: null,
            notes: null, // Don't add validation messages to notes - store in metadata instead
            blocking_reason: null,
            insufficient_content: false,
            is_blocked: false,
            social_media: null,
            enrichment_metadata: validationError ? {
              validation_failed: true,
              validation_reason: validationError,
              tokens_used: 0,
              cost: 0,
              agent: 'validation',
              model: 'none'
            } : undefined
          } as any;
          
          handleProgress('⚠️ Validation failed early - creating minimal camp entry for manual completion');
        }

        if (!enrichmentResponse) {
          setResult({
            success: false,
            message: 'Enrichment failed - no response received'
          });
          return;
        }

        // If we have enrichment response, proceed with saving
        // Note: We'll mark as inactive if validation failed or if blocked/insufficient content
        const shouldMarkInactive = validationFailed || 
                                   enrichmentResponse?.is_blocked || 
                                   enrichmentResponse?.blocking_reason ||
                                   enrichmentResponse?.insufficient_content;
        
        if (shouldMarkInactive) {
          if (validationFailed) {
            handleProgress('⚠️ Validation failed - camp will be saved as inactive for manual review');
          } else {
            handleProgress('⚠️ Content issues detected - camp will be saved as inactive for review');
          }
        }

        // Show preview of extracted data (if we have any)
        if (enrichmentResponse.description || enrichmentResponse.phone || enrichmentResponse.email) {
          handleProgress('✨ Enrichment data available:');
        } else {
          handleProgress('⚠️ Limited enrichment data - camp will need manual completion');
        }
        
        // Debug: Log what name extraction found
        console.log('[ENRICHMENT-MODAL] Name extraction debug:', {
          aiExtractedName: enrichmentResponse.aiExtractedName,
          facebookName: enrichmentResponse.facebookName,
          hasDescription: !!enrichmentResponse.description,
          descriptionLength: enrichmentResponse.description?.length || 0,
          hasBulletList: !!enrichmentResponse.bulletList,
          bulletListLength: enrichmentResponse.bulletList?.length || 0
        });
        
        if (enrichmentResponse.description) {
          const descPreview = enrichmentResponse.description.substring(0, 80);
          handleProgress(`📝 Description: "${descPreview}${enrichmentResponse.description.length > 80 ? '...' : ''}"`);
        }
        
        // Try to extract name from description or bulletList early if AI didn't extract it
        if (!enrichmentResponse.aiExtractedName && !enrichmentResponse.facebookName) {
          handleProgress('🔍 Attempting to extract camp name from content...');
          
          // Try multiple sources and patterns
          const contentSources = [
            enrichmentResponse?.description || '',
            enrichmentResponse?.bulletList || '',
          ].filter(Boolean);
          
          // More aggressive patterns to find names like "Derek Marinatos Soccer Academy"
          const namePatterns = [
            // Pattern 1: Person name + Sport + Academy (e.g., "Derek Marinatos Soccer Academy")
            /([A-Z][a-z]+(?:\s+[A-Z][a-z]+)+\s+(?:Soccer|Tennis|Swim|Sports|Athletic)\s+(?:Academy|Club|Center|School|Institute|Facility))/i,
            // Pattern 2: Any capitalized name + Academy/Club/etc (e.g., "Planet Swim & Tennis Club")
            /([A-Z][A-Za-z\s&]{5,60}(?:Academy|Club|Center|School|Institute|Facility))/i,
            // Pattern 3: Look for names in headings (starts with #)
            /#\s*([A-Z][A-Za-z\s&]{5,60}(?:Academy|Club|Center|School|Institute|Facility))/i,
            // Pattern 4: Look for names in bold/strong tags
            /\*\*([A-Z][A-Za-z\s&]{5,60}(?:Academy|Club|Center|School|Institute|Facility))\*\*/i,
          ];
          
          for (const content of contentSources) {
            for (const pattern of namePatterns) {
              const match = content.match(pattern);
              if (match && match[1]) {
                const extractedName = match[1].trim();
                // Validate: must have at least 2 words, not be domain-like
                if (extractedName.length >= 10 && 
                    extractedName.split(/\s+/).length >= 2 &&
                    extractedName.length < 100 &&
                    !/^[a-z]+[a-z0-9]*$/i.test(extractedName)) {
                  // Store it in the response object so it can be used later
                  if (enrichmentResponse) {
                    enrichmentResponse.aiExtractedName = extractedName;
                  }
                  handleProgress(`✅ Found camp name in content: "${extractedName}"`);
                  console.log('[ENRICHMENT-MODAL] ✅ Extracted name from content:', extractedName);
                  break;
                }
              }
            }
            if (enrichmentResponse?.aiExtractedName) break;
          }
        }
        
        if (enrichmentResponse?.address || enrichmentResponse?.city || enrichmentResponse?.state) {
          const addressParts = [
            enrichmentResponse.address,
            enrichmentResponse.city,
            enrichmentResponse.state,
            enrichmentResponse.zip_code
          ].filter(Boolean);
          if (addressParts.length > 0) {
            handleProgress(`📍 Address: ${addressParts.join(', ')}`);
          }
        }
        
        if (enrichmentResponse?.social_media) {
          const socialLinks = Object.entries(enrichmentResponse.social_media)
            .filter(([_, url]) => url)
            .map(([platform]) => platform);
          if (socialLinks.length > 0) {
            handleProgress(`📱 Social media found: ${socialLinks.join(', ')}`);
          } else {
            handleProgress(`📱 No social media links found`);
          }
        }
        
        if (enrichmentResponse?.activities && enrichmentResponse.activities.length > 0) {
          const activitiesPreview = enrichmentResponse.activities.slice(0, 5).join(', ');
          const moreCount = enrichmentResponse.activities.length > 5 ? ` +${enrichmentResponse.activities.length - 5} more` : '';
          handleProgress(`🎯 Activities: ${activitiesPreview}${moreCount}`);
        }
        
        if (enrichmentResponse?.dates_available) {
          const dates = Array.isArray(enrichmentResponse.dates_available) 
            ? enrichmentResponse.dates_available 
            : [enrichmentResponse.dates_available];
          if (dates.length > 0) {
            const datesPreview = dates.slice(0, 3).join(', ');
            const moreDates = dates.length > 3 ? ` +${dates.length - 3} more` : '';
            handleProgress(`📅 Dates: ${datesPreview}${moreDates}`);
          }
        }
        
        handleProgress('💾 Saving new camp to database...');

        // Helper function to safely convert to array for database
        const ensureArray = (value: string | string[] | null | undefined): string[] | null => {
          if (!value) return null;
          if (Array.isArray(value)) {
            const filtered = value.filter(item => item && typeof item === 'string' && item.trim().length > 0);
            return filtered.length > 0 ? filtered : null;
          }
          if (typeof value === 'string') {
            const parsed = parseListText(value);
            return parsed.length > 0 ? parsed : null;
          }
          return null;
        };

        // Prepare camp data from enriched response
        // Extract a better name from multiple sources (priority order)
        // IMPORTANT: Prioritize AI-extracted names over domain parsing to avoid bad names
        let campName = placeholderName;
        
        // Strategy 0: Use Facebook name (most reliable - official organization name)
        if (enrichmentResponse?.facebookName) {
          campName = enrichmentResponse.facebookName.trim();
          console.log(`[CAMP-NAME] Using Facebook name: "${campName}"`);
        }
        
        // Strategy 0.5: Use AI-extracted name (intelligent extraction from content)
        // This should be the primary source - AI extracts proper names from website content
        if (campName === placeholderName && enrichmentResponse?.aiExtractedName) {
          const extractedName = enrichmentResponse.aiExtractedName.trim();
          console.log(`[CAMP-NAME] Checking AI-extracted name: "${extractedName}"`);
          
          // Validate the extracted name - reject if it looks like a domain name (single word, all lowercase)
          // Examples of bad names to reject: "dmsocceracademy", "planetswimtennisclub"
          // Good names have spaces and proper capitalization: "Derek Marinatos Soccer Academy"
          const isDomainLike = /^[a-z]+[a-z0-9]*$/i.test(extractedName) && extractedName.split(/\s+/).length === 1;
          
          // Reject facility/location names (e.g., "Including Turf Field at Duval Charter School")
          const isFacilityName = /^(including|at|located at|held at|featuring|with|near|in)\s+/i.test(extractedName) ||
                                 /\s+(at|in|near|located at|held at)\s+[A-Z]/i.test(extractedName) ||
                                 /(field|facility|location|venue|site|building|center|center|school|university|park|stadium|arena)\s+(at|in)/i.test(extractedName);
          
          // Reject if it's clearly a location description rather than a camp name
          const isLocationDescription = extractedName.split(/\s+/).length > 6 && 
                                        (extractedName.toLowerCase().includes('at ') || 
                                         extractedName.toLowerCase().includes('in ') ||
                                         extractedName.toLowerCase().includes('located'));
          
          const isValid = extractedName && 
              extractedName !== 'NOT_FOUND' && 
              extractedName.length > 3 && 
              extractedName.length < 100 &&
              !isDomainLike &&
              !isFacilityName &&
              !isLocationDescription;
          
          if (isValid) {
            campName = extractedName;
            console.log(`[CAMP-NAME] ✅ Using AI-extracted name: "${campName}"`);
          } else {
            console.warn(`[CAMP-NAME] ⚠️ AI-extracted name "${extractedName}" was rejected (isDomainLike: ${isDomainLike}, isFacilityName: ${isFacilityName}, isLocationDescription: ${isLocationDescription})`);
            // Don't give up yet - we'll try other strategies
          }
        }
        
        // Strategy 1: DO NOT extract from website domain - it creates bad names like "Dmsocceracademy"
        // If we don't have an AI-extracted name or Facebook name, we should fail
        if (campName === placeholderName) {
          console.error(`[CAMP-NAME] ❌ No valid camp name found - AI extraction failed or returned invalid name`);
          // We'll handle this in Strategy 4 below - fail the enrichment
        }
        
        // Strategy 2: Check bulletList for organization/club names (avoid activity names)
        if (campName === placeholderName && enrichmentResponse?.bulletList) {
          // Look for organization/club names - avoid activity names like "Junior Tennis Camps"
          // Pattern: Look for names ending with "Club", "Center", "Academy", "School" (not "Camps" or "Programs")
          // Also look for person name + sport + academy pattern (e.g., "Derek Marinatos Soccer Academy")
          const orgNamePatterns = [
            /\b([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*\s+(?:Soccer|Tennis|Swim|Sports|Athletic)\s+(?:Academy|Club|Center|School|Institute|Facility))\b/i, // Person name + sport + type
            /\b([A-Z][A-Za-z\s&]{3,50}(?:Club|Center|Academy|School|Institute|Facility))\b/, // General organization pattern
          ];
          
          for (const pattern of orgNamePatterns) {
            const match = enrichmentResponse.bulletList.match(pattern);
            if (match && match[1]) {
              const extractedName = match[1].trim();
              // Validate it's not an activity name
              if (extractedName.length >= 5 && 
                  extractedName.length < 80 && 
                  extractedName.split(/\s+/).length >= 2 &&
                  extractedName.split(/\s+/).length <= 8 &&
                  !/^(junior|adult|kids|children|youth|teen|summer|winter|spring|fall|this|the|a|an)\s+/i.test(extractedName) &&
                  !/^(offers|provides|features|is|has|will|can)\s+/i.test(extractedName) &&
                  !/camp(s)?$/i.test(extractedName)) { // Don't use names ending with "Camp" or "Camps"
                campName = extractedName;
                console.log(`[CAMP-NAME] ✅ Extracted organization name from bulletList: "${campName}"`);
                break;
              }
            }
          }
        }
        
        // Strategy 3: Try to extract camp name from description (avoid activity names)
        if (campName === placeholderName && enrichmentResponse?.description) {
          // Look for organization/club names, not activity names
          // Specifically look for "Derek Marinatos Soccer Academy" pattern
          const orgNamePatterns = [
            /\b([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*\s+(?:Soccer|Tennis|Swim|Sports|Athletic)\s+(?:Academy|Club|Center|School|Institute|Facility))\b/i, // Person name + sport + type
            /\b([A-Z][A-Za-z\s&]{3,50}(?:Club|Center|Academy|School|Institute|Facility))\b/, // General organization pattern
          ];
          
          for (const pattern of orgNamePatterns) {
            const match = enrichmentResponse.description.match(pattern);
            if (match && match[1]) {
              const extractedName = match[1].trim();
              
              // Reject facility/location names
              const isFacilityName = /^(including|at|located at|held at|featuring|with|near|in)\s+/i.test(extractedName) ||
                                     /\s+(at|in|near|located at|held at)\s+[A-Z]/i.test(extractedName);
              
              if (extractedName.length >= 5 && 
                  extractedName.length < 80 && 
                  extractedName.split(/\s+/).length >= 2 &&
                  extractedName.split(/\s+/).length <= 8 &&
                  !/^(junior|adult|kids|children|youth|teen|summer|winter|spring|fall|this|the|a|an)\s+/i.test(extractedName) &&
                  !/^(offers|provides|features|is|has|will|can)\s+/i.test(extractedName) &&
                  !/^(including|at|located at|held at|featuring|with|near|in)\s+/i.test(extractedName) &&
                  !/camp(s)?$/i.test(extractedName) &&
                  !isFacilityName) {
                campName = extractedName;
                console.log(`[CAMP-NAME] ✅ Extracted organization name from description: "${campName}"`);
                break;
              }
            }
          }
          
          // Pattern 2: Look for camp name in quotes
          if (campName === placeholderName && enrichmentResponse?.description) {
            const quotedName = enrichmentResponse.description.match(/"([^"]{5,60}(?:Club|Center|Academy|School|Institute)[^"]{0,30})"/i);
            if (quotedName && quotedName[1]) {
              const extractedName = quotedName[1].trim();
              if (extractedName.length >= 5 && extractedName.length < 80 && extractedName.split(/\s+/).length >= 2) {
                campName = extractedName;
                console.log(`[CAMP-NAME] ✅ Extracted from quotes: "${campName}"`);
              }
            }
          }
        }
        
        // Strategy 4: Last resort - try extracting from the raw website content if we have it
        if (campName === placeholderName) {
          console.warn(`[CAMP-NAME] ⚠️ All extraction strategies failed, trying last resort...`);
          
          // Try to extract from the website URL as a very last resort (only if it has multiple words)
          try {
            // Normalize URL for parsing - add protocol if missing
            let urlForParsing = website.trim();
            if (urlForParsing && !urlForParsing.match(/^https?:\/\//i)) {
              urlForParsing = `https://${urlForParsing}`;
            }
            const urlObj = new URL(urlForParsing);
            const hostname = urlObj.hostname.replace('www.', '').replace(/\.[^.]+$/, '');
            
            // Only use URL if it has multiple words separated by dashes
            if (hostname.includes('-') && hostname.split('-').length >= 3) {
              const urlName = hostname
                .split('-')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
              
              // Only use if it looks reasonable (has multiple words, not too short)
              if (urlName.length >= 10 && urlName.split(/\s+/).length >= 3) {
                campName = urlName;
                console.log(`[CAMP-NAME] ⚠️ Using URL-derived name as last resort: "${campName}"`);
                handleProgress(`⚠️ Using name derived from website URL: "${campName}" (please verify and edit if needed)`);
              }
            }
          } catch (urlError) {
            console.warn('[CAMP-NAME] Failed to parse URL for last resort name:', urlError);
          }
        }
        
        // Strategy 5: If we still have the placeholder, fail
        if (campName === placeholderName || campName.length > 80) {
          // If AI didn't extract a proper name, we need to fail or require manual entry
          console.error(`[CAMP-NAME] ❌ All extraction strategies failed - no valid name found`);
          console.error(`[CAMP-NAME] Debug info:`, {
            aiExtractedName: enrichmentResponse?.aiExtractedName,
            facebookName: enrichmentResponse?.facebookName,
            hasDescription: !!enrichmentResponse?.description,
            descriptionPreview: enrichmentResponse?.description?.substring(0, 200),
            hasBulletList: !!enrichmentResponse?.bulletList,
            bulletListPreview: enrichmentResponse?.bulletList?.substring(0, 200),
            website: website,
            validationFailed
          });
          
          // If validation failed, still save with placeholder name - human can fix it
          if (validationFailed) {
            handleProgress('⚠️ Could not extract camp name - will save with placeholder name for manual entry');
            // Keep placeholderName - human will fix it in the admin panel
          } else {
            setResult({
              success: false,
              message: 'Could not extract camp name from website. The AI extraction failed and no fallback patterns matched. Please enter the camp name manually (e.g., "Derek Marinatos Soccer Academy").'
            });
            setIsEnriching(false);
            return;
          }
        }
        
        // Final validation: Clean up the name
        // Remove quotes if AI wrapped it
        campName = campName.replace(/^["']|["']$/g, '').trim();
        
        // Additional validation: Reject names that look like domain names (single word, all lowercase)
        // This catches cases where the AI might have extracted "dmsocceracademy" instead of "Derek Marinatos Soccer Academy"
        if (campName && /^[a-z]+[a-z0-9]*$/i.test(campName) && campName.split(/\s+/).length === 1) {
          console.error(`[CAMP-NAME] ⚠️ Extracted name "${campName}" looks like a domain name (single word, no spaces)`);
          setResult({
            success: false,
            message: `Could not extract a proper camp name from website. The system found "${campName}" which appears to be incorrect. Please enter the camp name manually.`
          });
          setIsEnriching(false);
          return;
        }
        
        // If name still looks like a sentence or is too generic, reject it
        if (campName && (campName.length > 80 || campName.split(/\s+/).length > 10 || /^(this|the|a|an|our|we|they|it|summer|winter|spring|fall)\s+/i.test(campName))) {
          console.error(`[CAMP-NAME] ⚠️ Extracted name "${campName}" looks invalid`);
          setResult({
            success: false,
            message: `Extracted camp name "${campName}" appears to be invalid. Please enter the camp name manually or try a different website URL.`
          });
          setIsEnriching(false);
          return;
        }
        
        // Clean up: Remove common descriptive words that might have been captured
        campName = campName
          .replace(/\s+(offers|provides|features|is|has|will|can|specializes|focuses|the|a|an)\s+/gi, ' ')
          .replace(/^\s*(the|a|an)\s+/i, '')
          .replace(/\s+(and|&)\s*$/i, '')
          .trim();
        
        // Final cleanup: Capitalize words properly, but preserve & and special characters
        campName = campName
          .split(/(\s+|&)/)
          .map((word, index) => {
            if (word === '&') return '&';
            if (word.trim().length === 0) return word;
            // Don't capitalize common words in the middle (unless it's the first word)
            const commonWords = ['the', 'and', 'of', 'for', 'in', 'on', 'at', 'to', 'a', 'an'];
            if (index > 0 && commonWords.includes(word.toLowerCase())) {
              return word.toLowerCase();
            }
            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
          })
          .join('');
        
        // Final validation: if name is still too long or looks like a sentence, reject it
        if (campName.length > 100 || campName.split(/\s+/).length > 10) {
          console.error(`[CAMP-NAME] ⚠️ Name "${campName}" is too long or invalid after cleanup`);
          setResult({
            success: false,
            message: `Could not extract a valid camp name from website. Please enter the camp name manually.`
          });
          setIsEnriching(false);
          return;
        }
        
        // Convert dates_available, activities, and amenities to arrays
        // IMPORTANT: Use dates_available from enrichmentResponse (should already be post-processed in aiEnrichment.ts)
        // The post-processing in aiEnrichment.ts should have fixed the dates and year (rejecting 2024, using 2025)
        const datesAvailableArray = ensureArray(enrichmentResponse?.dates_available);
        const activitiesArray = ensureArray(enrichmentResponse?.activities);
        const amenitiesArray = ensureArray(enrichmentResponse?.amenities);

        // Debug log to see what we're about to insert
        console.log('🔍 [ENRICHMENT-MODAL] Preparing camp data:', {
          camp_name: campName,
          dates_available: datesAvailableArray,
          activities: activitiesArray,
          amenities: amenitiesArray,
          city: enrichmentResponse?.city,
          state: enrichmentResponse?.state,
          address: enrichmentResponse?.address,
          suggested_categories: enrichmentResponse?.suggested_categories,
          dates_available_type: typeof enrichmentResponse?.dates_available,
          dates_available_isArray: Array.isArray(enrichmentResponse?.dates_available),
          dates_available_raw: enrichmentResponse?.dates_available,
          validationFailed,
          shouldMarkInactive
        });
        
        // Log dates for debugging - check if they contain 2024 (which should have been fixed)
        if (datesAvailableArray && datesAvailableArray.length > 0) {
          const has2024Dates = datesAvailableArray.some(d => d && d.toString().includes('2024'));
          if (has2024Dates) {
            console.warn('⚠️ [ENRICHMENT-MODAL] WARNING: Dates still contain 2024 - post-processing may have failed:', datesAvailableArray);
          } else {
            console.log('✅ [ENRICHMENT-MODAL] Dates look correct (no 2024 dates found):', datesAvailableArray);
          }
        }

        // Extract city, state, zip from address if they're missing
        // First, use values from enrichmentResponse if available (handle empty strings as missing)
        let extractedCity = enrichmentResponse?.city?.trim() || null;
        let extractedState = enrichmentResponse?.state?.trim() || null;
        let extractedZip = enrichmentResponse?.zip_code?.trim() || null;
        let cleanedAddress = enrichmentResponse?.address?.trim() || null;
        
        // Treat empty strings as null
        if (extractedCity === '') extractedCity = null;
        if (extractedState === '') extractedState = null;
        if (extractedZip === '') extractedZip = null;
        if (cleanedAddress === '') cleanedAddress = null;
        
        // Convert state abbreviation to full name (e.g., "FL" -> "Florida")
        if (extractedState) {
          extractedState = convertStateAbbreviationToFullName(extractedState);
          console.log(`[STATE-CONVERSION] Converted state to full name: "${extractedState}"`);
        }
        
        // Clean up address if it contains placeholder text
        if (cleanedAddress && (cleanedAddress.includes('City to be updated') || cleanedAddress.includes('Address to be updated'))) {
          // Remove placeholder text from address
          cleanedAddress = cleanedAddress
            .replace(/\s*City to be updated\s*,?/gi, '')
            .replace(/\s*Address to be updated\s*,?/gi, '')
            .replace(/,\s*,/g, ',') // Remove double commas
            .trim();
        }
        
        // If any of city/state/zip are missing, try to extract from address string
        // This handles cases where the AI extracted the full address but didn't split it properly
        // Common formats: 
        // - "300 Davis Park Rd, Ponte Vedra, FL 32081"
        // - "123 Street, City, ST 12345"
        // - "123 Street, City, State"
        // - "123 Street, City, ST"
        // - "City, ST 12345"
        if (cleanedAddress && (!extractedCity || !extractedState || !extractedZip)) {
          const address = cleanedAddress.trim();
          const addressParts = address.split(',').map(part => part.trim()).filter(part => part.length > 0);
          
          if (addressParts.length >= 2) {
            // Try to parse the last part which usually contains city, state, zip
            const lastPart = addressParts[addressParts.length - 1];
            
            // Pattern 1: "Ponte Vedra FL 32081" or "City ST 12345" or "City ST"
            // Match city name (can contain spaces like "Ponte Vedra"), then state (2 letters), then optional zip
            const cityStateZipMatch = lastPart.match(/^([A-Za-z\s]+?)\s+([A-Z]{2})(?:\s+(\d{5}(?:-\d{4})?))?$/);
            if (cityStateZipMatch) {
              // City is in the last part before state (e.g., "Ponte Vedra")
              if (!extractedCity) extractedCity = cityStateZipMatch[1].trim();
              if (!extractedState) extractedState = cityStateZipMatch[2];
              if (!extractedZip && cityStateZipMatch[3]) extractedZip = cityStateZipMatch[3];
              // Remove city/state/zip from address to get just the street address
              cleanedAddress = addressParts.slice(0, -1).join(', ').trim();
            } else {
              // Pattern 2: Last part is just state + zip, second-to-last is city
              // Check if last part looks like a state (2 letters) + zip
              const stateZipMatch = lastPart.match(/^([A-Z]{2})(?:\s+(\d{5}(?:-\d{4})?))?$/);
              if (stateZipMatch && addressParts.length >= 3) {
                // City is the second-to-last part (e.g., "Ponte Vedra" in "300 Davis Park Rd, Ponte Vedra, FL 32081")
                if (!extractedCity) extractedCity = addressParts[addressParts.length - 2];
                if (!extractedState) extractedState = stateZipMatch[1];
                if (!extractedZip && stateZipMatch[2]) extractedZip = stateZipMatch[2];
                // Remove city and state from address to get just the street address
                cleanedAddress = addressParts.slice(0, -2).join(', ').trim();
              } else if (addressParts.length >= 2) {
                // Pattern 3: Last part might be state only, second-to-last is city
                // Check if last part is a 2-letter state code
                if (/^[A-Z]{2}$/.test(lastPart) && addressParts.length >= 3) {
                  if (!extractedCity) extractedCity = addressParts[addressParts.length - 2];
                  if (!extractedState) extractedState = lastPart;
                  // Remove city and state from address
                  cleanedAddress = addressParts.slice(0, -2).join(', ').trim();
                } else {
                  // Fallback: assume second-to-last part is city if it doesn't look like a street address
                  const possibleCity = addressParts[addressParts.length - 2];
                  // If it doesn't contain numbers and is not a state abbreviation, it's likely a city name
                  if (!extractedCity && !/\d/.test(possibleCity) && !/^[A-Z]{2}$/.test(possibleCity) && possibleCity.length > 2) {
                    extractedCity = possibleCity;
                    // Try to extract state from last part
                    if (!extractedState) {
                      const lastPartStateMatch = lastPart.match(/\b([A-Z]{2})\b/);
                      if (lastPartStateMatch) {
                        extractedState = lastPartStateMatch[1];
                      }
                    }
                    // Try to extract zip from last part
                    if (!extractedZip) {
                      const lastPartZipMatch = lastPart.match(/\b(\d{5}(?:-\d{4})?)\b/);
                      if (lastPartZipMatch) {
                        extractedZip = lastPartZipMatch[1];
                      }
                    }
                    // Remove city from address
                    cleanedAddress = addressParts.slice(0, -2).concat(addressParts.slice(-1)).join(', ').trim();
                  }
                }
              }
            }
          } else if (addressParts.length === 1 && address.includes(',')) {
            // Single part address with comma, might be "City, ST" format (comma in city name like "St. Louis, MO")
            const singlePartMatch = address.match(/^(.+?),\s*([A-Z]{2})(?:\s+(\d{5}(?:-\d{4})?))?$/);
            if (singlePartMatch) {
              if (!extractedCity) extractedCity = singlePartMatch[1].trim();
              if (!extractedState) extractedState = singlePartMatch[2];
              if (!extractedZip && singlePartMatch[3]) extractedZip = singlePartMatch[3];
              cleanedAddress = ''; // No street address, just city
            }
          }
          
          // Convert state abbreviation to full name after parsing (if not already converted)
          if (extractedState) {
            const stateBeforeConversion = extractedState;
            extractedState = convertStateAbbreviationToFullName(extractedState);
            if (stateBeforeConversion !== extractedState) {
              console.log(`[STATE-CONVERSION] Converted state from address parsing: "${stateBeforeConversion}" -> "${extractedState}"`);
            }
          }
          
          console.log('🔍 [ENRICHMENT-MODAL] Address parsing:', {
            original_address: enrichmentResponse.address,
            cleaned_address: cleanedAddress,
            extracted_city: extractedCity,
            extracted_state: extractedState,
            extracted_zip: extractedZip,
            ai_city: enrichmentResponse.city,
            ai_state: enrichmentResponse.state,
            ai_zip: enrichmentResponse.zip_code,
            address_parts_count: addressParts?.length || 0
          });
        }

        // Normalize website URL - ensure it has a protocol
        let normalizedWebsite = website.trim();
        if (normalizedWebsite && !normalizedWebsite.match(/^https?:\/\//i)) {
          // Add https:// if no protocol is present
          normalizedWebsite = `https://${normalizedWebsite}`;
          console.log(`[ENRICHMENT-MODAL] Normalized website URL: "${website.trim()}" -> "${normalizedWebsite}"`);
        }

        const campData: any = {
          name: campName, // Use improved name (or placeholder if extraction failed)
          description: enrichmentResponse?.description || null,
          short_description: enrichmentResponse?.short_description || null,
          website_url: normalizedWebsite || null,
          address: cleanedAddress || enrichmentResponse?.address || null,
          city: extractedCity || null,
          state: extractedState || null,
          zip_code: extractedZip || null,
          latitude: enrichmentResponse?.latitude || null,
          longitude: enrichmentResponse?.longitude || null,
          phone: enrichmentResponse?.phone || null,
          email: enrichmentResponse?.email || null,
          min_age: enrichmentResponse?.min_age ? Math.round(Number(enrichmentResponse.min_age)) : null,
          max_age: enrichmentResponse?.max_age ? Math.round(Number(enrichmentResponse.max_age)) : null,
          start_hour_1: enrichmentResponse?.start_hour_1 || null,
          finish_hour_1: enrichmentResponse?.finish_hour_1 || null,
          start_hour_2: enrichmentResponse?.start_hour_2 || null,
          finish_hour_2: enrichmentResponse?.finish_hour_2 || null,
          extended_time_available: enrichmentResponse?.extended_time_available ?? null,
          lunch_provided: enrichmentResponse?.lunch_provided ?? null,
          lunch_fee: enrichmentResponse?.lunch_fee ? Math.round(Number(enrichmentResponse.lunch_fee)) : null,
          five_day_cost: enrichmentResponse?.five_day_cost ? Math.round(Number(enrichmentResponse.five_day_cost)) : null,
          dates_available: datesAvailableArray, // This should be string[] or null
          notes: enrichmentResponse?.notes || null, // Don't add validation messages to notes - stored in enrichment_metadata instead
          activities: activitiesArray, // This should be string[] or null
          amenities: amenitiesArray, // This should be string[] or null
          blocking_reason: enrichmentResponse?.blocking_reason || null,
          insufficient_content: enrichmentResponse?.insufficient_content ?? false,
          active: !shouldMarkInactive, // Set as inactive if validation failed or content issues
          // Save social_media directly to database column (like import flow does)
          social_media: enrichmentResponse?.social_media || null,
          ai_enriched: {
            enrichments: [
              {
                timestamp: new Date().toISOString(),
                version: 'v2',
                tokens_used: enrichmentResponse?.enrichment_metadata?.tokens_used || 0,
                cost: enrichmentResponse?.enrichment_metadata?.cost || 0,
                agent: enrichmentResponse?.enrichment_metadata?.agent || 'gpt-4',
                model: enrichmentResponse?.enrichment_metadata?.model || 'gpt-4o',
                enrichment_mode: 'create',
                // Store validation info in the enrichment record if validation failed
                ...(validationFailed && validationError ? {
                  validation_failed: true,
                  validation_reason: validationError
                } : {})
              }
            ],
            total_enrichments: 1,
            last_enriched: new Date().toISOString()
          }
          // Note: enrichment_metadata is not a database column - metadata is stored in ai_enriched JSONB
        };

        // Database requires: name, address, city, state, zip_code (NOT NULL)
        // IMPORTANT: Don't make up addresses - if address is missing, save as unpublished
        const hasCompleteAddress = campData.address && campData.city && campData.state && campData.zip_code;
        
        if (!hasCompleteAddress) {
          handleProgress('⚠️ Missing location data - camp will be saved as unpublished (please add address manually)');
          // Don't use placeholder values - use empty strings for required NOT NULL fields
          // but mark as unpublished so it's not shown publicly until address is added
          campData.active = false;
          
          // Use empty strings for missing required fields (database may require NOT NULL)
          // This is better than placeholder text - empty strings indicate missing data
          if (!campData.address) campData.address = '';
          if (!campData.city) campData.city = '';
          if (!campData.state) campData.state = '';
          if (!campData.zip_code) campData.zip_code = '';
          
          // Log what's missing for debugging
          console.warn('⚠️ [ENRICHMENT-MODAL] Missing address components - camp saved as unpublished:', {
            hasAddress: !!campData.address,
            hasCity: !!campData.city,
            hasState: !!campData.state,
            hasZip: !!campData.zip_code
          });
        }

        // Geocode address if coordinates are missing or invalid
        // Check if coordinates are valid (not 0,0 and not center of US fallback)
        const centerOfUSLat = 39.8283;
        const centerOfUSLon = -98.5795;
        const hasValidCoordinates = campData.latitude && 
          campData.longitude && 
          campData.latitude !== 0 && 
          campData.longitude !== 0 &&
          !(Math.abs(campData.latitude - centerOfUSLat) < 0.0001 && Math.abs(campData.longitude - centerOfUSLon) < 0.0001);
        
        // Prepare geocoding parameters (skip placeholders)
        const geocodeAddressParam = campData.address && campData.address !== 'Address to be updated' ? campData.address : undefined;
        const geocodeCityParam = campData.city && campData.city !== 'City to be updated' ? campData.city : undefined;
        const geocodeStateParam = campData.state && campData.state !== 'ST' ? campData.state : undefined;
        const geocodeZipParam = campData.zip_code && campData.zip_code !== '00000' ? campData.zip_code : undefined;
        
        // Construct full address string for better geocoding accuracy
        // This ensures we use the complete address (street + city + state + zip) for geocoding
        const fullAddressParts: string[] = [];
        if (geocodeAddressParam) fullAddressParts.push(geocodeAddressParam);
        if (geocodeCityParam) fullAddressParts.push(geocodeCityParam);
        if (geocodeStateParam) fullAddressParts.push(geocodeStateParam);
        if (geocodeZipParam) fullAddressParts.push(geocodeZipParam);
        const fullAddressString = fullAddressParts.join(', ');
        
        // Try to geocode if we have at least some location info (city+state minimum, or address)
        // IMPORTANT: Only geocode if we have a complete address - don't geocode incomplete addresses
        // Prioritize geocoding if we have full address (street + city + state + zip) for best accuracy
        const hasFullAddress = geocodeAddressParam && geocodeCityParam && geocodeStateParam;
        const hasLocationInfo = (geocodeCityParam && geocodeStateParam) || geocodeAddressParam;
        
        // Only geocode if we have valid coordinates missing AND complete address info
        // Don't geocode incomplete addresses to avoid making up locations
        if (!hasValidCoordinates && hasFullAddress) {
          try {
            handleProgress('🗺️ Geocoding address to get coordinates...');
            console.log('🗺️ [ENRICHMENT-MODAL] Geocoding with:', {
              street_address: geocodeAddressParam,
              city: geocodeCityParam,
              state: geocodeStateParam,
              zip: geocodeZipParam,
              full_address_string: fullAddressString,
              hasFullAddress,
              currentCoordinates: [campData.latitude, campData.longitude]
            });
            
            // Use the full address string for better accuracy
            // The geocodeAddress function will try structured geocoding first (with separate components)
            // and fall back to free-form search (which will use the full address string we construct)
            const geocodeResult = await geocodeAddress(
              geocodeAddressParam, // Street address
              geocodeCityParam,    // City
              geocodeStateParam,   // State (now full name like "Florida")
              geocodeZipParam      // Zip
            );

            // Check if geocoding succeeded and coordinates are not fallback center of US
            const isFallbackCenter = Math.abs(geocodeResult.latitude - centerOfUSLat) < 0.0001 && 
                                     Math.abs(geocodeResult.longitude - centerOfUSLon) < 0.0001;
            
            if (geocodeResult.accuracy !== 'failed' && 
                geocodeResult.latitude !== 0 && 
                geocodeResult.longitude !== 0 &&
                !isFallbackCenter) {
              campData.latitude = geocodeResult.latitude;
              campData.longitude = geocodeResult.longitude;
              handleProgress(`✅ Coordinates found: ${geocodeResult.latitude.toFixed(6)}, ${geocodeResult.longitude.toFixed(6)} (${geocodeResult.accuracy} accuracy)`);
              console.log('✅ [ENRICHMENT-MODAL] Geocoded coordinates successfully:', {
                address: geocodeAddressParam,
                city: geocodeCityParam,
                state: geocodeStateParam,
                zip: geocodeZipParam,
                coordinates: [geocodeResult.latitude, geocodeResult.longitude],
                accuracy: geocodeResult.accuracy,
                source: geocodeResult.source
              });
            } else {
              console.warn('⚠️ [ENRICHMENT-MODAL] Geocoding returned invalid/fallback coordinates:', {
                latitude: geocodeResult.latitude,
                longitude: geocodeResult.longitude,
                accuracy: geocodeResult.accuracy,
                source: geocodeResult.source,
                isFallbackCenter
              });
              handleProgress('⚠️ Could not geocode address accurately - coordinates will be set to 0,0 (please edit after creation)');
              campData.latitude = 0;
              campData.longitude = 0;
            }
          } catch (geocodeError) {
            console.error('❌ [ENRICHMENT-MODAL] Geocoding failed:', geocodeError);
            handleProgress('⚠️ Geocoding failed - coordinates will be set to 0,0 (please edit after creation)');
            campData.latitude = 0;
            campData.longitude = 0;
          }
        } else if (!hasValidCoordinates) {
          // No complete address information available - don't geocode incomplete addresses
          if (!hasFullAddress) {
            handleProgress('⚠️ Incomplete address - coordinates not geocoded (please add complete address and edit after creation)');
          } else {
            handleProgress('⚠️ No address information available - coordinates set to 0,0 (please edit after creation)');
          }
          campData.latitude = 0;
          campData.longitude = 0;
        } else {
          handleProgress(`✅ Using coordinates from enrichment: ${campData.latitude.toFixed(6)}, ${campData.longitude.toFixed(6)}`);
          console.log('🗺️ [ENRICHMENT-MODAL] Using coordinates from enrichment:', {
            coordinates: [campData.latitude, campData.longitude]
          });
        }

        // Insert the new camp
        const { data: newCamp, error: insertError } = await supabase
          .from('camps')
          .insert([campData])
          .select()
          .single();

        if (insertError) {
          console.error('Error creating camp:', insertError);
          setResult({
            success: false,
            message: `Failed to create camp: ${insertError.message}`
          });
          return;
        }

        handleProgress('✅ Camp created successfully!');

        // Save enrichment data to project directory via API
        try {
          const enrichmentDataFile = {
            camp_id: newCamp.id,
            camp_name: newCamp.name,
            camp_slug: newCamp.slug,
            website_url: website.trim(),
            enrichment_timestamp: new Date().toISOString(),
            enrichment_mode: 'create',
            enrichment_response: enrichmentResponse,
            enrichment_metadata: enrichmentResponse.enrichment_metadata || {},
            saved_camp_data: campData,
          };

          handleProgress('💾 Saving enrichment data to project files...');
          
          const saveResponse = await fetch('/api/save-enrichment-data', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ enrichmentData: enrichmentDataFile }),
          });

          if (saveResponse.ok) {
            const saveResult = await saveResponse.json();
            handleProgress(`💾 Enrichment data saved to: ${saveResult.filename}`);
            console.log('📁 [ENRICHMENT-MODAL] Saved enrichment data to:', saveResult.filepath);
          } else {
            const errorData = await saveResponse.json().catch(() => ({}));
            console.warn('⚠️ [ENRICHMENT-MODAL] Failed to save enrichment data file:', errorData.error || 'Unknown error');
            // Don't fail the whole operation if file save fails
          }
        } catch (fileError) {
          console.error('⚠️ [ENRICHMENT-MODAL] Failed to save enrichment data file:', fileError);
          // Don't fail the whole operation if file save fails
        }

        // Handle categories if provided in enrichment response
        if (enrichmentResponse.suggested_categories && newCamp) {
          handleProgress('📂 Setting up categories...');
          
          // Handle suggested_categories structure (can have focus_area, format, age_group, etc.)
          const allCategoryNames: string[] = [];
          if (enrichmentResponse.suggested_categories.focus_area) {
            allCategoryNames.push(...enrichmentResponse.suggested_categories.focus_area);
          }
          if (enrichmentResponse.suggested_categories.format) {
            allCategoryNames.push(...enrichmentResponse.suggested_categories.format);
          }
          if (enrichmentResponse.suggested_categories.age_group) {
            allCategoryNames.push(...enrichmentResponse.suggested_categories.age_group);
          }
          
          console.log('🔍 [ENRICHMENT-MODAL] Suggested categories to match:', allCategoryNames);
          
          // Find and link categories (limit to 5 to get more matches)
          if (allCategoryNames.length > 0) {
            let linkedCount = 0;
            for (const categoryName of allCategoryNames.slice(0, 5)) {
              // Try multiple matching strategies
              // 1. Exact match (case-insensitive)
              let { data: categories, error: searchError } = await supabase
                .from('camp_categories')
                .select('id, name')
                .ilike('name', categoryName)
                .limit(1);

              // 2. Partial match if exact match fails
              if (!categories || categories.length === 0) {
                const { data: partialMatches } = await supabase
                  .from('camp_categories')
                  .select('id, name')
                  .ilike('name', `%${categoryName}%`)
                  .limit(3);
                categories = partialMatches;
              }

              // 3. Try word-by-word matching for multi-word categories
              if ((!categories || categories.length === 0) && categoryName.includes(' ')) {
                const words = categoryName.split(/\s+/).filter(w => w.length > 3);
                for (const word of words) {
                  const { data: wordMatches } = await supabase
                    .from('camp_categories')
                    .select('id, name')
                    .ilike('name', `%${word}%`)
                    .limit(1);
                  if (wordMatches && wordMatches.length > 0) {
                    categories = wordMatches;
                    break;
                  }
                }
              }

              console.log(`🔍 [ENRICHMENT-MODAL] Category "${categoryName}": Found ${categories?.length || 0} matches`, categories?.map(c => c.name));

              if (categories && categories.length > 0) {
                try {
                  const { error: relationError } = await supabase
                    .from('camp_category_relations')
                    .insert([{
                      camp_id: newCamp.id,
                      category_id: categories[0].id
                    }]);

                  if (relationError) {
                    // Ignore duplicate key errors (PostgreSQL error code 23505)
                    if (relationError.code === '23505' || relationError.message?.includes('duplicate')) {
                      console.log(`✅ [ENRICHMENT-MODAL] Category "${categories[0].name}" already linked (duplicate ignored)`);
                      linkedCount++;
                    } else {
                      console.error('❌ [ENRICHMENT-MODAL] Error linking category:', relationError);
                    }
                  } else {
                    console.log(`✅ [ENRICHMENT-MODAL] Linked category "${categories[0].name}"`);
                    linkedCount++;
                  }
                } catch (err) {
                  // Ignore duplicate key errors
                  const error = err as any;
                  if (error?.code === '23505' || error?.message?.includes('duplicate')) {
                    console.log(`✅ [ENRICHMENT-MODAL] Category already linked (duplicate ignored)`);
                    linkedCount++;
                  } else {
                    console.error('❌ [ENRICHMENT-MODAL] Error linking category:', err);
                  }
                }
              } else {
                console.warn(`⚠️ [ENRICHMENT-MODAL] No category found for "${categoryName}"`);
              }
            }
            
            if (linkedCount > 0) {
              handleProgress(`✅ Linked ${linkedCount} categor${linkedCount === 1 ? 'y' : 'ies'}`);
            } else {
              handleProgress('⚠️ No categories matched - you can add them manually in the camp editor');
            }
          } else {
            console.log('ℹ️ [ENRICHMENT-MODAL] No suggested categories in enrichment response');
          }
        }

        setResult({
          success: true,
          message: `Camp "${newCamp.name}" created and enriched successfully!`
        });

        // Trigger success callback and close modal
        onSuccess();
        setTimeout(() => {
          onClose();
          setResult(null);
          setProgress('');
          setWebsite('');
        }, 2000);

        return;
      }

      // Mode is 'enrich' - enrich existing camp
      if (!campId) {
        throw new Error('Camp ID is required for enrichment mode');
      }

      const request: EnrichExistingCampRequest = {
        campId,
        website: website.trim()
      };

      const response = await enrichExistingCamp(request, handleProgress);

      setResult(response);
      
      if (response.success && response.enrichedData) {
        // Check if there's existing content - if so, show review step
        const hasExistingContent = response.enrichedData.originalCamp && (
          response.enrichedData.originalCamp.description ||
          response.enrichedData.originalCamp.activities?.length ||
          response.enrichedData.originalCamp.amenities?.length
        );

        if (hasExistingContent && mode === 'enrich') {
          // Show review step - don't save yet
          setEnrichmentData(response.enrichedData);
          setEditedUpdateData({ ...response.enrichedData.updateData }); // Initialize editable data
          setShowReview(true);
          setIsEnriching(false);
        } else {
          // No existing content or create mode - save directly
          await handleSaveEnrichment(response.enrichedData, campId);
        }
      }
    } catch (error) {
      setResult({
        success: false,
        message: `Enrichment failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setIsEnriching(false);
    }
  };

  const handleSaveEnrichment = async (enrichedData: any, campId?: string) => {
    if (!campId) return;

    try {
      // Save to database
      const saveResult = await saveEnrichedCamp(campId, enrichedData.updateData, enrichedData.enrichmentResponse);
      
      if (!saveResult.success) {
        setResult({ success: false, message: saveResult.message });
        return;
      }

      // Save enrichment data to project directory via API
      try {
        const { data: updatedCamp } = await supabase
          .from('camps')
          .select('id, name, slug')
          .eq('id', campId)
          .single();

        if (updatedCamp) {
          const enrichmentDataFile = {
            camp_id: updatedCamp.id,
            camp_name: updatedCamp.name,
            camp_slug: updatedCamp.slug,
            website_url: website.trim(),
            enrichment_timestamp: new Date().toISOString(),
            enrichment_mode: 'enrich_existing',
            enrichment_response: enrichedData.enrichmentResponse || enrichedData,
            update_data: enrichedData.updateData || null,
            original_camp_data: enrichedData.originalCamp || null,
          };

          const apiUrl = '/api/save-enrichment-data';
          const saveResponse = await fetch(apiUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ enrichmentData: enrichmentDataFile }),
          });

          if (saveResponse.ok) {
            const saveResult = await saveResponse.json();
            console.log('📁 [ENRICHMENT-MODAL] Saved enrichment data to:', saveResult.filepath);
          }
        }
      } catch (fileError) {
        console.error('⚠️ [ENRICHMENT-MODAL] Failed to save enrichment data file:', fileError);
      }

      onSuccess();
      setTimeout(() => {
        onClose();
        setResult(null);
        setProgress('');
        setShowReview(false);
        setEnrichmentData(null);
      }, 1000);
    } catch (error) {
      setResult({
        success: false,
        message: `Failed to save: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    }
  };

  const handleApproveReview = async () => {
    if (!enrichmentData || !campId) return;
    setIsEnriching(true);
    
    // Use edited data if available, otherwise use original updateData
    const dataToSave = editedUpdateData 
      ? { ...enrichmentData, updateData: editedUpdateData }
      : enrichmentData;

    await handleSaveEnrichment(dataToSave, campId);
    setIsEnriching(false);
  };

  const handleCancelReview = () => {
    setShowReview(false);
    setEnrichmentData(null);
    setEditedUpdateData(null);
    setResult(null);
    setProgress('');
  };

  const handleClose = () => {
    if (!isEnriching && !showReview) {
      onClose();
      setResult(null);
      setProgress('');
      setWebsite(currentWebsite || '');
      setShowReview(false);
      setEnrichmentData(null);
    }
  };

  return (
    <Transition appear show={isOpen} as={React.Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className={`w-full transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all ${
                showReview ? 'max-w-6xl' : 'max-w-md'
              }`}>
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900 flex items-center">
                    <SparklesIcon className="h-5 w-5 mr-2 text-green-500" />
                    AI Enrichment
                  </Dialog.Title>
                  <button
                    onClick={handleClose}
                    disabled={isEnriching}
                    className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="mb-4">
                  {campName && (
                    <p className="text-sm text-gray-600 mb-2">
                      Enriching: <span className="font-medium">{campName}</span>
                    </p>
                  )}
                  {mode === 'create' && (
                    <p className="text-sm text-gray-500 mb-4">
                      Enter the camp's website URL to automatically create and enrich the camp listing using AI.
                    </p>
                  )}
                  <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-1">
                    Website URL
                  </label>
                  <input
                    type="url"
                    id="website"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    placeholder="https://example.com"
                    disabled={isEnriching}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                </div>

                {progressLogs.length > 0 && (
                  <div className="mb-4 bg-gray-900 rounded-lg overflow-hidden border border-gray-700 shadow-lg">
                    <div className="px-4 py-2 bg-gray-800 border-b border-gray-700 flex items-center justify-between">
                      <span className="text-gray-400 text-xs font-semibold">Terminal Output</span>
                      <span className="text-gray-500 text-xs">{progressLogs.length} lines</span>
                    </div>
                    <div 
                      className="p-4 text-green-400 font-mono text-xs max-h-96 overflow-y-auto bg-gray-900"
                      ref={(el) => {
                        // Auto-scroll to bottom when new logs are added
                        if (el) {
                          el.scrollTop = el.scrollHeight;
                        }
                      }}
                      style={{
                        scrollbarWidth: 'thin',
                        scrollbarColor: '#4a5568 #1a202c'
                      }}
                    >
                      {progressLogs.map((log, index) => (
                        <div 
                          key={index} 
                          className="mb-1 leading-relaxed hover:text-green-300 transition-colors"
                          style={{ 
                            wordBreak: 'break-word',
                            whiteSpace: 'pre-wrap'
                          }}
                        >
                          {log}
                        </div>
                      ))}
                      {isEnriching && (
                        <div className="inline-flex items-center text-green-500 animate-pulse">
                          <span className="mr-2">█</span>
                          <span>Processing...</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {showReview && enrichmentData ? (
                  <div className="mt-4">
                    <h4 className="text-md font-semibold mb-4">Review Changes - Compare Old vs New</h4>
                    <div className="max-h-96 overflow-y-auto overflow-x-auto border border-gray-200 rounded-lg">
                      <table className="w-full divide-y divide-gray-200 text-sm table-fixed" style={{ minWidth: '800px' }}>
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 w-1/6">Field</th>
                            <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 w-2/5">Current (Old)</th>
                            <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 w-2/5">New (Enriched)</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {[
                            ['Description', enrichmentData.originalCamp?.description, enrichmentData.updateData?.description],
                            ['Short Description', enrichmentData.originalCamp?.short_description, enrichmentData.updateData?.short_description],
                            ['Activities', JSON.stringify(enrichmentData.originalCamp?.activities || []), JSON.stringify(enrichmentData.updateData?.activities || [])],
                            ['Amenities', JSON.stringify(enrichmentData.originalCamp?.amenities || []), JSON.stringify(enrichmentData.updateData?.amenities || [])],
                            ['Email', enrichmentData.originalCamp?.email, enrichmentData.updateData?.email],
                            ['Phone', enrichmentData.originalCamp?.phone, enrichmentData.updateData?.phone],
                            ['Address', enrichmentData.originalCamp?.address, enrichmentData.updateData?.address],
                            ['Notes', enrichmentData.originalCamp?.notes, enrichmentData.updateData?.notes],
                          ].map(([label, oldValue, newValue], idx) => {
                            // Map label to actual field name in the data structure
                            const getFieldName = (lbl: string): string => {
                              if (lbl === 'Description') return 'description';
                              if (lbl === 'Short Description') return 'short_description';
                              if (lbl === 'Activities') return 'activities';
                              if (lbl === 'Amenities') return 'amenities';
                              if (lbl === 'Email') return 'email';
                              if (lbl === 'Phone') return 'phone';
                              if (lbl === 'Address') return 'address';
                              if (lbl === 'Notes') return 'notes';
                              return lbl.toLowerCase().replace(/\s+/g, '_');
                            };
                            
                            const fieldName = getFieldName(label as string);
                            const isChanged = oldValue !== newValue;
                            const displayOld = oldValue || '—';
                            // Use edited value if available, otherwise use original newValue
                            const currentNewValue = editedUpdateData?.[fieldName] !== undefined 
                              ? editedUpdateData[fieldName] 
                              : newValue;
                            const displayNew = currentNewValue || '—';
                            const isLongTextField = ['Description', 'Short Description', 'Notes'].includes(label as string);
                            
                            // Handle field updates
                            const handleFieldChange = (value: any) => {
                              if (!editedUpdateData) return;
                              
                              const updated = { ...editedUpdateData };
                              
                              // Handle special cases for arrays
                              if (label === 'Activities') {
                                try {
                                  updated.activities = JSON.parse(value);
                                } catch {
                                  updated.activities = value.split(',').map((s: string) => s.trim()).filter(Boolean);
                                }
                              } else if (label === 'Amenities') {
                                try {
                                  updated.amenities = JSON.parse(value);
                                } catch {
                                  updated.amenities = value.split(',').map((s: string) => s.trim()).filter(Boolean);
                                }
                              } else {
                                // Use the field name directly
                                updated[fieldName] = value;
                              }
                              
                              setEditedUpdateData(updated);
                            };
                            
                            return (
                              <tr key={idx} className={isChanged ? 'bg-yellow-50' : ''} style={{ minHeight: isLongTextField ? '200px' : 'auto' }}>
                                <td className="px-4 py-2 font-medium text-gray-900 align-top w-1/6">{label}</td>
                                <td className={`px-4 py-2 align-top w-2/5 ${isChanged ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                                  {isLongTextField ? (
                                    <div className="max-h-[300px] overflow-y-auto overflow-x-hidden">
                                      <div className="text-sm whitespace-pre-wrap break-words">
                                        {displayOld}
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="break-words">{displayOld}</div>
                                  )}
                                </td>
                                <td className="px-4 py-2 text-gray-900 align-top w-2/5">
                                  {isLongTextField ? (
                                    <textarea
                                      value={displayNew}
                                      onChange={(e) => handleFieldChange(e.target.value)}
                                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm min-h-[200px] resize-y"
                                      rows={8}
                                    />
                                  ) : label === 'Activities' || label === 'Amenities' ? (
                                    <input
                                      type="text"
                                      value={typeof displayNew === 'string' ? displayNew : JSON.stringify(displayNew || [])}
                                      onChange={(e) => handleFieldChange(e.target.value)}
                                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                    />
                                  ) : (
                                    <input
                                      type="text"
                                      value={displayNew}
                                      onChange={(e) => handleFieldChange(e.target.value)}
                                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                    />
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div className="flex justify-end space-x-3 mt-4">
                      <button
                        onClick={handleCancelReview}
                        disabled={isEnriching}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleApproveReview}
                        disabled={isEnriching}
                        className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 flex items-center"
                      >
                        {isEnriching ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Saving...
                          </>
                        ) : (
                          'Approve & Save'
                        )}
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    {result && (
                      <div className={`mb-4 p-3 border rounded-md ${
                        result.success 
                          ? 'bg-green-50 border-green-200' 
                          : 'bg-red-50 border-red-200'
                      }`}>
                        <p className={`text-sm ${
                          result.success ? 'text-green-800' : 'text-red-800'
                        }`}>
                          {result.message}
                        </p>
                      </div>
                    )}

                    <div className="flex justify-end space-x-3 mt-4">
                      <button
                        onClick={handleClose}
                        disabled={isEnriching}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleEnrich}
                        disabled={isEnriching || !website.trim()}
                        className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                      >
                        {isEnriching ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Enriching...
                          </>
                        ) : (
                          <>
                            <SparklesIcon className="h-4 w-4 mr-2" />
                            Enrich Camp
                          </>
                        )}
                      </button>
                    </div>
                  </>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
